import React, { useState, useEffect } from 'react';
import { Box, Typography, Alert, Chip, Grid, Card, CardContent, IconButton, Collapse, Stepper, Step, StepLabel } from '@mui/material';
import { PiBug, PiCaretDown, PiCaretUp } from 'react-icons/pi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/apiService';

import DownloadOutputs from './DownloadOutputs';
import UserInputForm from './UserInputForm';
import ExecutionDebug from './ExecutionDebug';
import ProgressBar from './ProgressBar';
import WorkflowResultData from './WorkflowResultData';
import { normalizeWorkflowData } from '../utils/workflowDataParsing';

interface ExecutionMonitorProps {
  executionArn: string;
  /** called whenever the execution status changes (for parent components that need to react) */
  onStatusChange?: (status: string, data: any) => void;
}

export default function ExecutionMonitor({ executionArn, onStatusChange }: ExecutionMonitorProps) {
  const [debugExpanded, setDebugExpanded] = useState(false);
  const [metadataExpanded, setMetadataExpanded] = useState(false);
  const queryClient = useQueryClient();
  
  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['execution', executionArn],
    queryFn: () => apiService.getExecutionStatus(executionArn),
    refetchInterval: (query) => {
      const currentStatus = (query.state.data as any)?.status;
      if (['SUCCEEDED', 'FAILED', 'CANCELLED', 'TIMED_OUT', 'ABORTED'].includes(currentStatus)) {
        return false;
      }
      return 3000;
    },
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Sync jobs when status changes
  useEffect(() => {
    if ((status as any)?.status && ['SUCCEEDED', 'FAILED', 'CANCELLED'].includes((status as any).status)) {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    }
    if ((status as any)?.status && onStatusChange) {
      onStatusChange((status as any).status, status);
    }
  }, [(status as any)?.status, queryClient, onStatusChange]);

  const { data: results } = useQuery({
    queryKey: ['results', executionArn],
    queryFn: () => apiService.getExecutionResults(executionArn),
    enabled: (status as any)?.status === 'SUCCEEDED',
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  // Build results from status output if available
  const fallbackResults = (status as any)?.output ? {
    files: ((status as any).output.output_files || []).map((file: any) => {
      const fileName = file.name || (file.s3_uri ? file.s3_uri.split('/').pop() : 'output_file.txt');
      return {
        name: fileName,
        size: file.size || 1024,
        type: file.s3_uri ? file.s3_uri.split('.').pop() : (file.name ? file.name.split('.').pop() : 'txt'),
        s3Uri: file.s3_uri,
        identifier: file.identifier || undefined
      };
    }),
    // surface data-only step outputs (e.g. cortese_list) so results still render with zero files
    step_data: (() => {
      // workflow_data may be an object or a JSON string, and Python handlers can emit bare NaN
      // (invalid JSON) — normalizeWorkflowData tolerates both.
      const wd = normalizeWorkflowData((status as any).output.workflow_data);
      if (!wd) return undefined;
      const skip = ['files', 'input_files', 'comment_workflow_data'];
      const searchedAddresses: string[] = Array.isArray(wd.addresses) ? wd.addresses : [];
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(wd)) {
        if (skip.includes(k)) continue;
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          const { params, ...rest } = v as any;
          if (Object.keys(rest).length > 0) {
            if (Array.isArray((rest as any).matches) && searchedAddresses.length > 0) {
              (rest as any).searched_addresses = searchedAddresses;
            }
            out[k] = rest;
          }
        }
      }
      return Object.keys(out).length > 0 ? out : undefined;
    })(),
    summary: {
      processingTime: Math.floor((new Date((status as any).stopDate || Date.now()).getTime() - new Date((status as any).startDate).getTime()) / 1000),
      documentsProcessed: (status as any).input?.input_file_s3_uris?.length ||
        ((status as any).input?.workflow_data?.input_files || (status as any).input?.workflow_data?.files || []).length || 0,
      outputFilesGenerated: ((status as any).output.output_files || []).length,
      executionStatus: (status as any).status,
      workflowType: (status as any).input?.workflows?.[0] || (status as any).input?.metadata?.workflow || 'unknown',
      practice: (status as any).input?.practice || (status as any).input?.metadata?.practice || 'unknown',
      tokensProcessed: (status as any).output.ai_processor_output?.tokens_used || (status as any).output.execution_summary?.tokens_processed,
      confidenceScore: (status as any).output.ai_processor_output?.confidence_score || (status as any).output.execution_summary?.confidence,
      entitiesExtracted: (status as any).output.ai_processor_output?.entities_count || (status as any).output.execution_summary?.entities_extracted
    }
  } : undefined;

  const finalResults = results ?? fallbackResults;

  const submitUserInputMutation = useMutation({
    mutationFn: (data: Record<string, any>) => 
      apiService.submitUserInput(executionArn, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['execution', executionArn] });
    }
  });

  if (statusLoading) {
    return (
      <Box sx={{ py: 2 }}>
        <ProgressBar height={100} />
      </Box>
    );
  }

  if (!status || !(status as any).status) {
    return <Alert severity="error" sx={{ mt: 1 }}>Failed to load execution status</Alert>;
  }

  const executionSteps = (() => {
    const v2Progress = (status as any)?.v2Progress;
    if (v2Progress?.stepNames?.length > 0) {
      // build step labels from recipe workflow step names
      return [
        'Preparing',
        ...v2Progress.stepNames.map((name: string) =>
          name.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
        ),
        'Complete'
      ];
    }
    return ['Upload', 'Recipe', 'Extract', 'AI Process', 'Results'];
  })();
  
  const getCurrentStep = () => {
    const v2Progress = (status as any)?.v2Progress;
    if ((status as any).status === 'SUCCEEDED') return executionSteps.length;
    if ((status as any).status === 'FAILED' || (status as any).status === 'TIMED_OUT') return -1;
    if ((status as any).status === 'WAITING_FOR_INPUT') return Math.floor(executionSteps.length / 2);
    
    // use real V2 progress when available
    if (v2Progress && v2Progress.totalSteps > 0) {
      // +1 because "Preparing" is step 0, workflow steps start at index 1
      return Math.min(v2Progress.currentStepIndex + 1, executionSteps.length - 1);
    }
    
    return Math.min((status as any).step || 0, executionSteps.length - 1);
  };

  const currentStep = getCurrentStep();
  const progressValue = (status as any).status === 'SUCCEEDED' ? 100 : 
                        (status as any).status === 'FAILED' ? 0 :
                        Math.round((currentStep / (executionSteps.length - 1)) * 100);

  const formatDuration = (seconds: number) => {
    if (!seconds || isNaN(seconds) || seconds < 0) return '0s';
    if (seconds < 60) return `${Math.floor(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins >= 60) {
      const hours = Math.floor(mins / 60);
      const remainMins = mins % 60;
      return `${hours}h ${remainMins}m`;
    }
    return `${mins}m ${secs}s`;
  };

  const runningDuration = (status as any).status === 'RUNNING' && (status as any).startDate
    ? Math.floor((Date.now() - new Date((status as any).startDate).getTime()) / 1000)
    : 0;

  return (
    <Box>
      {/* Debug Panel (dev only) */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <IconButton 
            size="small"
            onClick={() => setDebugExpanded(!debugExpanded)}
            color={debugExpanded ? 'primary' : 'default'}
            title="Toggle Debug Info"
          >
            <PiBug size={16} />
          </IconButton>
        </Box>
      )}
      
      {process.env.NODE_ENV === 'development' && debugExpanded && (
        <Alert severity="warning" sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
          <ExecutionDebug status={status} currentStep={currentStep} />
        </Alert>
      )}

      {/* RUNNING State */}
      {(status as any).status === 'RUNNING' && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Chip label="Processing" color="primary" size="small" />
            <Typography variant="caption" color="text.secondary">
              {formatDuration(runningDuration)}
            </Typography>
          </Box>
          
          <ProgressBar value={progressValue} height={100} />
          
          <Stepper activeStep={currentStep} alternativeLabel sx={{ mt: 2 }}>
            {executionSteps.map((label, index) => (
              <Step key={label} completed={index < currentStep}>
                <StepLabel 
                  sx={{ 
                    '& .MuiStepLabel-label': { fontSize: '0.7rem' },
                    '& .MuiStepIcon-root': { fontSize: '1.2rem' }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      {/* WAITING_FOR_INPUT State */}
      {(status as any).status === 'WAITING_FOR_INPUT' && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Workflow paused - additional information required to continue.
            </Typography>
          </Alert>
          {(status as any).userInputRequest && (
            <UserInputForm
              inputRequest={(status as any).userInputRequest}
              onSubmit={(data) => submitUserInputMutation.mutate(data)}
              loading={submitUserInputMutation.isPending}
            />
          )}
        </Box>
      )}

      {/* SUCCEEDED State */}
      {(status as any).status === 'SUCCEEDED' && finalResults && (
        <Box>
          {/* Summary Stats - Compact Row */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                  <Typography variant="h6" color="primary" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                    {formatDuration(finalResults.summary.processingTime)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Time
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                  <Typography variant="h6" color="primary" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                    {finalResults.summary.documentsProcessed}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Input{finalResults.summary.documentsProcessed !== 1 ? 's' : ''}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                  <Typography variant="h6" color="primary" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                    {finalResults.summary.outputFilesGenerated || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Output{(finalResults.summary.outputFilesGenerated || 0) !== 1 ? 's' : ''}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {finalResults.summary.tokensProcessed && (
              <Grid item xs={6} sm={3}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                    <Typography variant="h6" color="primary" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      {(finalResults.summary.tokensProcessed / 1000).toFixed(1)}k
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tokens
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Expandable Metadata */}
          <Collapse in={metadataExpanded}>
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" display="block">
                <strong>Workflow:</strong> {finalResults.summary.workflowType}
              </Typography>
              <Typography variant="caption" display="block">
                <strong>Practice:</strong> {finalResults.summary.practice}
              </Typography>
              <Typography variant="caption" display="block">
                <strong>Execution ID:</strong> {executionArn.split(':').pop()}
              </Typography>
              <Typography variant="caption" display="block">
                <strong>Completed:</strong> {new Date((status as any).stopDate).toLocaleString()}
              </Typography>
              {finalResults.summary.confidenceScore && (
                <Typography variant="caption" display="block">
                  <strong>Confidence:</strong> {(finalResults.summary.confidenceScore * 100).toFixed(1)}%
                </Typography>
              )}
            </Box>
          </Collapse>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Chip label="Completed" color="success" size="small" />
            <Box 
              onClick={() => setMetadataExpanded(!metadataExpanded)} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' }
              }}
            >
              <Typography variant="caption">
                {metadataExpanded ? 'Hide' : 'Show'} details
              </Typography>
              {metadataExpanded ? <PiCaretUp size={14} /> : <PiCaretDown size={14} />}
            </Box>
          </Box>
          
          {/* Download Outputs - Primary Action */}
          <DownloadOutputs 
            files={finalResults.files} 
            executionArn={executionArn}
            status={status}
          />

          {/* Inline data results (e.g. cortese_list matches) when the workflow produces no files */}
          {(finalResults as any).step_data && (
            <WorkflowResultData stepData={(finalResults as any).step_data} />
          )}
        </Box>
      )}

      {/* FAILED State */}
      {(status as any).status === 'FAILED' && (
        <Alert severity="error">
          <Typography variant="subtitle2" gutterBottom>Execution Failed</Typography>
          {(status as any).error && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'monospace', 
                fontSize: '0.8rem', 
                mt: 1, 
                p: 1, 
                bgcolor: 'rgba(0,0,0,0.05)', 
                borderRadius: 1,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {typeof (status as any).error === 'string' 
                ? (status as any).error 
                : JSON.stringify((status as any).error, null, 2)}
            </Typography>
          )}
        </Alert>
      )}

      {/* TIMED_OUT State */}
      {(status as any).status === 'TIMED_OUT' && (
        <Alert severity="warning">
          <Typography variant="subtitle2">Execution Timed Out</Typography>
          <Typography variant="body2">
            The workflow exceeded the maximum allowed execution time.
          </Typography>
        </Alert>
      )}

      {/* CANCELLED State */}
      {(status as any).status === 'CANCELLED' && (
        <Alert severity="info">
          <Typography variant="subtitle2">Execution Cancelled</Typography>
          <Typography variant="body2">
            This workflow was cancelled before completion.
          </Typography>
        </Alert>
      )}
    </Box>
  );
}